import DeviceDetector from 'device-detector-js'
import { openDB } from 'idb'
import { v4 as uuidv4 } from 'uuid'

import { deleteApiData, fetchApiData, postApiData, putApiData } from '@/helpers/api'
import { notificationDecision, notificationRequest, start } from '@/helpers/ws'

const tables = ['DOCUMENTS', 'USERS', 'REQUESTS', 'KEYS']

const dbPromise = (database, table) => {
  return openDB(database, getCurrentDBVersion(), {
    upgrade (db, oldVersion) {
      if (oldVersion < getCurrentDBVersion()) {
        tables.forEach(t => {
          if (!db.objectStoreNames.contains(t) && tables.includes(t)) {
            db.createObjectStore(t, { keyPath: 'id' })
          }
        })
      }
    }
  })
}

const getCurrentDBVersion = () => {
  return process.env.VUE_APP_INDEXED_DB_VERSION
}

export default {
  async unsubscribeAllSnapshots ({ state, commit }) {
    state.unsubscribes.forEach(unsubscribe => unsubscribe())
    commit('clearAllUnsubscribes')
  },
  clearItems ({ commit }, { modules = [] }) {
    commit('clearItems', { modules })
  },
  async get ({ state, commit }, { database, table, id }) {
    const db = await dbPromise(database, table)
    const tx = db.transaction(table, 'readonly')
    const store = tx.objectStore(table)
    return await store.get(id)
  },
  async getByUserId ({ state, commit }, { database, table, id, userId }) {
    const db = await dbPromise(database, table)
    const tx = db.transaction(table, 'readonly')
    const store = tx.objectStore(table)
    const keys = await store.getAll()
    let found = null
    keys.forEach(key => {
      if (key.userId === userId && key.id === id) {
        found = key
      }
    })
    return found
  },
  async gets ({ state, commit }, { database, table }) {
    const db = await dbPromise(database, table)
    const tx = db.transaction(table, 'readonly')
    const store = tx.objectStore(table)
    return await store.getAll()
  },
  async add ({ state, commit }, { database, table, record }) {
    const db = await dbPromise(database, table)
    const tx = db.transaction(table, 'readwrite')
    const store = tx.objectStore(table)
    await store.put(record)
    await tx.done
  },
  async update ({ state, commit }, { database, table, id, record }) {
    const db = await dbPromise(database, table)
    const tx = db.transaction(table, 'readwrite')
    const store = tx.objectStore(table)
    if (await store.get(id)) {
      await store.put(record)
    }
    return store.get(id)
  },
  async del ({ state, commit }, { database, table, id }) {
    const db = await dbPromise(database, table)
    const tx = db.transaction(table, 'readwrite')
    const store = tx.objectStore(table)
    await store.delete(id)
  },
  async fetchData ({ state, commit, rootGetters }, { stream }) {
    try {
      return await fetchApiData(`api/${stream}`)
    } catch (error) {
      console.warn(`[STORE] fetchData failed for stream: api/${stream}`, error.message)
      // Return graceful fallback to prevent component crashes
      return { error: error.message, data: null }
    }
  },
  async fetchDataWithParams ({ state, commit, rootGetters }, { stream, params }) {
    try {
      const searchParams = new URLSearchParams()
      Object.entries(params || {}).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, item ?? ''))
        } else {
          searchParams.append(key, value ?? '')
        }
      })
      const query = searchParams.toString()
      return await fetchApiData(query ? `${stream}?${query}` : stream)
    } catch (error) {
      console.warn(`[STORE] fetchDataWithParams failed for stream: ${stream}`, error.message)
      // Return graceful fallback to prevent component crashes
      return { error: error.message, data: null }
    }
  },
  async fetchDataWithURL ({ state, commit, rootGetters }, { stream, params }) {
    try {
      return await fetchApiData(`${stream}/${encodeURIComponent(String(params || ''))}`)
    } catch (error) {
      console.warn(`[STORE] fetchDataWithURL failed for stream: ${stream}/${params}`, error.message)
      // Return graceful fallback to prevent component crashes
      return { error: error.message, data: null }
    }
  },
  async createData ({ state, commit, rootGetters }, { stream, body, headers }) {
    return await postApiData(stream, body, headers)
  },
  async postData ({ state, commit, rootGetters }, { stream, data, headers }) {
    return await postApiData(stream, data, headers)
  },
  async updateData ({ state, commit, rootGetters }, { stream, body, headers }) {
    return await putApiData(stream, body, headers)
  },
  async putData ({ state, commit, rootGetters }, { stream, data, headers }) {
    return await putApiData(stream, data, headers)
  },
  async deleteData ({ state, commit, rootGetters }, { stream }) {
    return await deleteApiData(stream)
  },
  startWebSocket (_, { callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed, deviceID, clientID }) {
    start(callbackError, callbackRequest, callbackResponse, callbackWebsocketClosed, deviceID, clientID)
  },
  async sendNotificationRequest (_, { requestID, clientID, geoLocation }) {
    return await notificationRequest(requestID, clientID, geoLocation)
  },
  async sendNotificationDecision (_, { requestID, clientID, approval }) {
    return await notificationDecision(requestID, clientID, approval)
  },
  async generateDeviceId () {
    const deviceId = uuidv4()
    const deviceDetector = new DeviceDetector()
    const deviceInfo = deviceDetector.parse(navigator.userAgent)
    const os = deviceInfo.os.name
    const screenSize = `${window.innerWidth}x${window.innerHeight}`
    const uniqueDeviceID = `${os}-${screenSize}-${deviceId}`
    return uniqueDeviceID
  },
  async getPublicKey ({ _ }, id) {
    return await fetchApiData(`api/public/key/${id}`)
  }
}
